import { useMemo, useState } from 'react';
import type { Workstream } from '../types/workstream';
import type { AssigneeFilter } from '../types/taskFilter';

interface WorkstreamFilterPanelProps {
  workstreams: Workstream[];
  selectedIDs: string[] | null;
  showAll: boolean;
  showArchived: boolean;
  onToggleShowAll: (v: boolean) => void;
  onToggleShowArchived: (v: boolean) => void;
  onToggleWorkstream: (wsID: string) => void;
  onSelectAll: (visibleIDs: string[]) => void;
  onSelectNone: (visibleIDs: string[]) => void;
  onClose: () => void;
  // Task filter props
  taskSearch: string;
  onTaskSearchChange: (v: string) => void;
  taskAssignees: Set<AssigneeFilter>;
  onToggleTaskAssignee: (a: AssigneeFilter) => void;
  showArchivedTasks: boolean;
  onToggleShowArchivedTasks: (v: boolean) => void;
  userInitials: string;
}

const toggleStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '4px',
  fontSize: '13px',
  color: '#24292f',
  cursor: 'pointer',
};

function ToggleSwitch({ on, onClick, disabled }: { on: boolean; onClick: () => void; disabled?: boolean }) {
  return (
    <span
      role="switch"
      aria-checked={on}
      onClick={disabled ? undefined : onClick}
      style={{
        display: 'inline-block',
        width: '28px',
        height: '16px',
        borderRadius: '8px',
        backgroundColor: on ? '#2da44e' : '#d0d7de',
        position: 'relative',
        transition: 'background-color 0.15s',
        cursor: disabled ? 'default' : 'pointer',
        opacity: disabled ? 0.5 : 1,
      }}
    >
      <span style={{
        position: 'absolute',
        top: '2px',
        left: on ? '14px' : '2px',
        width: '12px',
        height: '12px',
        borderRadius: '50%',
        backgroundColor: '#fff',
        transition: 'left 0.15s',
      }} />
    </span>
  );
}

const iconBtnStyle: React.CSSProperties = {
  background: 'none',
  border: '1px solid #d0d7de',
  borderRadius: '4px',
  width: '24px',
  height: '24px',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  padding: 0,
};

export default function WorkstreamFilterPanel({
  workstreams, selectedIDs, showAll, showArchived,
  onToggleShowAll, onToggleShowArchived, onToggleWorkstream,
  onSelectAll, onSelectNone, onClose,
  taskSearch, onTaskSearchChange, taskAssignees, onToggleTaskAssignee,
  showArchivedTasks, onToggleShowArchivedTasks, userInitials,
}: WorkstreamFilterPanelProps) {
  const selectionDisabled = showAll;
  const effectiveSelected = selectedIDs ?? workstreams.map((ws) => ws.workstreamID);
  const selectedSet = new Set(effectiveSelected);
  const [searchText, setSearchText] = useState('');
  const visibleWorkstreams = useMemo(() => {
    // When Show All is on, ignore search text so the full list is
    // visible even if a previous query is still in the input.
    if (showAll) return workstreams;
    const q = searchText.trim().toLowerCase();
    if (!q) return workstreams;
    return workstreams.filter((ws) => ws.name.toLowerCase().includes(q));
  }, [workstreams, searchText, showAll]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <button
          type="button" onClick={onClose} title="Close filters" aria-label="Close filters"
          style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '2px', lineHeight: 1 }}
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#24292f" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="15 18 9 12 15 6" />
          </svg>
        </button>
        <span style={{ fontSize: '18px', fontWeight: 700 }}>Filters</span>
      </div>

      {/* Workstreams section */}
      <div>
        <div style={{ fontSize: '15px', fontWeight: 700, marginBottom: '8px' }}>Workstreams</div>

        {/* Show All + Show Archived toggles */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '10px' }}>
          <label style={toggleStyle}>
            Show All
            <ToggleSwitch on={showAll} onClick={() => onToggleShowAll(!showAll)} />
          </label>
          <label style={toggleStyle}>
            Show Archived
            <ToggleSwitch on={showArchived} onClick={() => onToggleShowArchived(!showArchived)} />
          </label>
        </div>

        {/* Search bar + Select All / Select None */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginBottom: '8px' }}>
          <div style={{
            flex: 1, display: 'flex', alignItems: 'center',
            border: '1px solid #24292f', borderRadius: '4px', padding: '4px 8px', gap: '4px',
            opacity: selectionDisabled ? 0.5 : 1,
          }}>
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#57606a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="11" cy="11" r="8" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
            <input
              type="text" placeholder="search" disabled={selectionDisabled}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              style={{ flex: 1, border: 'none', outline: 'none', fontSize: '13px', padding: 0, backgroundColor: 'transparent' }}
            />
          </div>
          <button type="button" title="Select All" aria-label="Select All"
            disabled={selectionDisabled}
            onClick={() => onSelectAll(visibleWorkstreams.map((ws) => ws.workstreamID))}
            style={{ ...iconBtnStyle, opacity: selectionDisabled ? 0.5 : 1, cursor: selectionDisabled ? 'default' : 'pointer' }}>
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#24292f" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              {/* Back square */}
              <rect x="2" y="2" width="14" height="14" rx="2" />
              {/* Front square */}
              <rect x="8" y="8" width="14" height="14" rx="2" />
              {/* Plus sign */}
              <line x1="15" y1="12" x2="15" y2="18" />
              <line x1="12" y1="15" x2="18" y2="15" />
            </svg>
          </button>
          <button type="button" title="Select None" aria-label="Select None"
            disabled={selectionDisabled}
            onClick={() => onSelectNone(visibleWorkstreams.map((ws) => ws.workstreamID))}
            style={{ ...iconBtnStyle, opacity: selectionDisabled ? 0.5 : 1, cursor: selectionDisabled ? 'default' : 'pointer' }}>
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#24292f" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              {/* Back square */}
              <rect x="2" y="2" width="14" height="14" rx="2" />
              {/* Front square */}
              <rect x="8" y="8" width="14" height="14" rx="2" />
              {/* Minus sign */}
              <line x1="12" y1="15" x2="18" y2="15" />
            </svg>
          </button>
        </div>

        {/* Workstream selection list */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          {visibleWorkstreams.length === 0 ? (
            <span style={{ fontSize: '13px', color: '#57606a', fontStyle: 'italic' }}>
              {workstreams.length === 0 ? 'No workstreams' : 'No matches'}
            </span>
          ) : (
            visibleWorkstreams.map((ws) => (
              <label
                key={ws.workstreamID}
                style={{
                  display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px',
                  cursor: selectionDisabled ? 'default' : 'pointer',
                  opacity: selectionDisabled ? 0.7 : 1,
                }}
              >
                <input
                  type="checkbox"
                  checked={showAll || selectedSet.has(ws.workstreamID)}
                  disabled={selectionDisabled}
                  onChange={() => onToggleWorkstream(ws.workstreamID)}
                  style={{ margin: 0 }}
                />
                {ws.name}
              </label>
            ))
          )}
        </div>
      </div>

      {/* Tasks section */}
      <div>
        <div style={{ fontSize: '15px', fontWeight: 700, marginBottom: '8px' }}>Tasks</div>

        {/* Show Archived toggle */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginBottom: '10px' }}>
          <label style={toggleStyle}>
            Show Archived
            <ToggleSwitch on={showArchivedTasks} onClick={() => onToggleShowArchivedTasks(!showArchivedTasks)} />
          </label>
        </div>

        {/* Task search bar */}
        <div style={{
          display: 'flex', alignItems: 'center',
          border: '1px solid #24292f', borderRadius: '4px', padding: '4px 8px', gap: '4px',
          marginBottom: '10px',
        }}>
          <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#57606a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="11" cy="11" r="8" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
          <input
            type="text" placeholder="search" value={taskSearch}
            onChange={(e) => onTaskSearchChange(e.target.value)}
            style={{ flex: 1, border: 'none', outline: 'none', fontSize: '13px', padding: 0, backgroundColor: 'transparent' }}
          />
        </div>

        {/* Assignee filter pills */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {/* Current user pill */}
          <button
            type="button"
            title="Your tasks"
            onClick={() => onToggleTaskAssignee('user')}
            style={{
              width: '32px', height: '32px', borderRadius: '50%', border: 'none', cursor: 'pointer',
              backgroundColor: taskAssignees.has('user') ? '#06b6d4' : '#d0d7de',
              color: '#fff', fontSize: '12px', fontWeight: 700, lineHeight: 1,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >
            {userInitials}
          </button>
          {/* AI (Plan 42) pill */}
          <button
            type="button"
            title="Plan 42 (AI) tasks"
            onClick={() => onToggleTaskAssignee('ai')}
            style={{
              width: '32px', height: '32px', borderRadius: '50%', border: 'none', cursor: 'pointer',
              backgroundColor: taskAssignees.has('ai') ? '#8b5cf6' : '#d0d7de',
              color: '#fff', fontSize: '12px', fontWeight: 700, lineHeight: 1,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >
            AI
          </button>
          {/* Unassigned pill */}
          <button
            type="button"
            title="Unassigned tasks"
            onClick={() => onToggleTaskAssignee('unassigned')}
            style={{
              width: '32px', height: '32px', borderRadius: '50%', border: 'none', cursor: 'pointer',
              backgroundColor: taskAssignees.has('unassigned') ? '#57606a' : '#d0d7de',
              color: '#fff', fontSize: '16px', fontWeight: 400, lineHeight: 1,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >
            ?
          </button>
        </div>
      </div>
    </div>
  );
}
